import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  TabsProps as ChakraTabsProps
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Tabs } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { fetchOrganization } from 'src/api/queries';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import UsersPanel from '../../SubmissionContexts/Users/UsersPanel';
import ReviewerSearchUsersPanel from './ReviewerSearchUsers/ReviewerSearchUsersPanel';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import Footer from '../../../../../components/Footer';
import WorkspaceMembersPanel from './WorkspaceMembersPanel';
import InvitedUsersPanel from './InvitedUsersPanel';
import MergeAccountsPanel from './MergeAccountsPanel';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
import { useWorkspaceData } from '../../../../../contexts/WorkspaceProvider';

const Members = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { shortname = '' } = useParams();
  const { workspaceId, orgId } = useWorkspace();
  const { workspace } = useWorkspaceData();

  const { isAllowed: canManage } = useAuthAccessQuery({
    relation: 'team_manager',
    entityType: 'workspace',
    entityId: workspaceId
  });
  const { isAllowed: canView } = useAuthAccessQuery({
    relation: 'canView',
    entityType: 'workspace',
    entityId: workspaceId
  });
  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => (shortname ? fetchOrganization(shortname) : null),
    enabled: Boolean(shortname),
    retry: 0
  });
  const chakraTabsProps = useMemo(
    () =>
      ({
        index: activeTabIndex,
        onChange: (index: number) => setActiveTabIndex(index)
      } as ChakraTabsProps),
    [activeTabIndex]
  );
  const allTabs = useMemo(
    () => [
      {
        label: 'Workspace Members',
        content: <WorkspaceMembersPanel workspaceId={workspaceId} />
      },
      {
        label: 'Manage Users',
        content: (
          <UsersPanel
            showAddUser
            showEditUser
            showEditIconOnly
            clampAffiliation
            showTimestamps
            backButtonText="Back to Manage Users"
            workspaceId={workspaceId}
            orgId={orgId}
          />
        )
      },
      {
        label: 'Reviewer Search Users',
        content: <ReviewerSearchUsersPanel workspaceId={workspaceId} />
      },
      {
        label: 'Invited Users',
        content: <InvitedUsersPanel workspaceId={workspaceId} />
      },
      ...(workspace.featureConfig?.accountMerge
        ? [
            {
              label: 'Merge Accounts',
              content: (
                <MergeAccountsPanel
                  workspaceId={workspaceId}
                  workspaceShortname={shortname}
                  organizationName={organization?.name}
                />
              )
            }
          ]
        : [])
    ],
    [orgId, organization?.name, shortname, workspaceId]
  );

  const tabs = useMemo(() => {
    if (canManage) {
      return allTabs;
    }
    if (canView) {
      return allTabs.filter(tab =>
        ['Manage Users', 'Reviewer Search Users'].includes(tab.label)
      );
    }
    return [];
  }, [allTabs, canManage, canView]);

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box padding={{ base: 2, md: 5 }} minHeight="100%">
        <Box display={{ base: 'block', md: 'none' }} marginBottom={4}>
          <Menu>
            <MenuButton
              borderWidth="0"
              borderRadius="0"
              variant="outline"
              as={Button}
              width="100%"
              justifyContent="space-between"
              textAlign="left"
              bg="white"
              border="1px solid"
              borderColor="neutralPrimaryBorder"
              rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
              _hover={{ bg: 'white' }}
              _active={{ bg: 'white' }}
            >
              {tabs[activeTabIndex]?.label}
            </MenuButton>
            <MenuList width="100%">
              <MenuGroup title="Manage Workspace">
                {tabs.map((tab, index) => (
                  <MenuItem
                    key={tab.label}
                    onClick={() => setActiveTabIndex(index)}
                    color={
                      activeTabIndex === index ? 'actionPrimaryFill' : undefined
                    }
                    fontWeight={
                      activeTabIndex === index ? 'semibold' : undefined
                    }
                  >
                    {tab.label}
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>
        </Box>
        <Box display={{ base: 'none', md: 'block' }}>
          <Tabs
            chakraTabsProps={chakraTabsProps}
            tabs={tabs.map(tab => ({ label: tab.label, content: null }))}
          />
        </Box>
        {tabs[activeTabIndex]?.content}
      </Box>
      <Footer />
      <ScrollToTopButton />
    </Box>
  );
};

export default Members;
